import React from 'react';
import classnames from 'classnames';
import * as styles from './review-response.less';
import { useDependencies } from '@servicetitan/react-ioc';
import { ServiceFinanceStore, ApplicationStatus } from '../../../../stores/service-finance.store';
import { Icon } from '@material-ui/core';
import Moment from 'moment';
import { formatMoney } from 'accounting';

interface StatusDisplay {
    icon: string;
    header: string;
    title: string;
    message?: string | undefined;
}

const getStatusDisplay = (status: ApplicationStatus | undefined): StatusDisplay => {
    switch (status) {
        case ApplicationStatus.Approved:
            return {
                icon: 'done',
                header: 'Congratulations, your loan has been approved!',
                title: 'Approved',
            };
        case ApplicationStatus.Complete:
            return {
                icon: 'done',
                header: 'Congratulations, your application is complete!',
                title: 'Complete',
            };
        case ApplicationStatus.Declined:
            return {
                icon: 'error_outline',
                header: 'Service Finance is unable to approve this application',
                title: 'Declined',
            };
        case ApplicationStatus.CounterOffer:
            return {
                icon: 'error_outline',
                header: 'Counteroffer received',
                title: 'Review',
                message:
                    'Please note, some of your plan details may have changed. Please review the details below',
            };
        case ApplicationStatus.UnderReview:
            return {
                icon: 'error_outline',
                header: 'Your application is under review',
                title: 'Under Review',
                message:
                    'For more details, contact Service Finance at 866-254-0497 option 4 for more details. \nHours of operations are from 5am ET - 11:59pm ET.',
            };
        default:
            return {
                icon: 'error_outline',
                header: 'Error communicating with Service Finance',
                title: 'Error',
                message:
                    'Please confirm your device is connected to the Internet and try again. \nOr contact Service Finance at 866-254-0497.',
            };
    }
};

export const ReviewResponse: React.FC = () => {
    const [store] = useDependencies(ServiceFinanceStore);
    const statusDisplay = getStatusDisplay(store.applicationStatus);
    const isDeclined = () =>
        store.applicationStatus === undefined ||
        store.applicationStatus === ApplicationStatus.Declined;

    const isUnderReview = () =>
        store.applicationStatus !== undefined &&
        store.applicationStatus === ApplicationStatus.UnderReview;

    const isApprovedOrCounterOffer = () =>
        store.applicationStatus !== undefined &&
        (store.applicationStatus === ApplicationStatus.Approved ||
            store.applicationStatus === ApplicationStatus.CounterOffer);

    const isCounterOffer = () =>
        store.applicationStatus !== undefined &&
        store.applicationStatus === ApplicationStatus.CounterOffer;
    return (
        <div>
            <Icon
                className={classnames(styles.icon, {
                    [styles.error]: isDeclined(),
                })}
            >
                {statusDisplay.icon}
            </Icon>
            <div className={styles.header}>{statusDisplay.header}</div>
            {statusDisplay.message && (
                <div className={styles.message}>{statusDisplay.message!}</div>
            )}
            <div>
                <table className={styles.table}>
                    <tbody>
                        <tr className={styles.row}>
                            <th className={styles.tableHeader}>Application ID</th>
                            <td className={styles.data}>{store.externalApplicationId}</td>
                        </tr>
                        <tr className={styles.row}>
                            <th className={styles.tableHeader}>Current Date</th>
                            <td className={styles.data}>{Moment(new Date()).format('L')}</td>
                        </tr>
                        <tr className={styles.row}>
                            <th className={styles.tableHeader}>Status</th>
                            <td className={styles.data}>{statusDisplay.title}</td>
                        </tr>
                        <tr className={styles.row}>
                            <th className={styles.tableHeader}>Finance Type</th>
                            <td className={styles.data}>{store.selectedPlan!.description}</td>
                        </tr>
                        {(isDeclined() || isUnderReview()) && (
                            <tr className={styles.row}>
                                <th className={styles.tableHeader}>Amount</th>
                                <td className={styles.data}>
                                    {formatMoney(store.getRequestLoanAmount())}
                                </td>
                            </tr>
                        )}
                        {isUnderReview() && (
                            <React.Fragment>
                                <tr className={styles.row}>
                                    <th className={styles.tableHeader}>Terms</th>
                                    <td className={styles.data}>-</td>
                                </tr>
                                <tr className={styles.row}>
                                    <th className={styles.tableHeader}>APR</th>
                                    <td className={styles.data}>-</td>
                                </tr>
                                <tr className={styles.row}>
                                    <th className={styles.tableHeader}>Monthly Payments</th>
                                    <td className={styles.data}>-</td>
                                </tr>
                            </React.Fragment>
                        )}
                        {isApprovedOrCounterOffer() && store.offer && (
                            <React.Fragment>
                                <tr className={styles.row}>
                                    <th className={styles.tableHeader}>Amount</th>
                                    <td className={styles.data}>
                                        {formatMoney(store.offer.AmountFinanced)}
                                    </td>
                                </tr>
                                <tr className={styles.row}>
                                    <th className={styles.tableHeader}>Terms</th>
                                    <td className={styles.data}>{store.offer.Term}</td>
                                </tr>
                                <tr className={styles.row}>
                                    <th className={styles.tableHeader}>APR</th>
                                    <td className={styles.data}>{store.offer.Apr}</td>
                                </tr>
                                <tr className={styles.row}>
                                    <th className={styles.tableHeader}>Monthly Payments</th>
                                    <td className={styles.data}>
                                        {formatMoney(store.offer.MonthlyPayment)}
                                    </td>
                                </tr>
                                {!isCounterOffer() && (
                                    <tr className={styles.row}>
                                        <th className={styles.tableHeader}>Max Credit Amount</th>
                                        <td className={styles.data}>
                                            {formatMoney(store.offer.MaxCreditAmount)}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
